import React, { useMemo, useState } from "react";
import useSWR from "swr";
import { Bar, Doughnut, HorizontalBar, Line } from "react-chartjs-2";
import { CircularProgress } from "@material-ui/core";
import 'chartjs-plugin-colorschemes';
import DashboardCard from "./cards/DashboardCard";
import { apiReports } from "../api/reports";

// Inflow bar colours (new vs renewal)
const COLOR_NEW = "rgba(0, 172, 220, 0.75)"; // blue
const COLOR_RENEWAL = "rgba(148, 186, 51, 0.75)"; // green
// Flow line colours
const COLOR_THROUGHPUT = "#45456e"; // dark slate
const COLOR_NETFLOW = "#d71433"; // red
const COLOR_TREND = "rgba(69, 69, 110, 0.8)"; // dashed slate — net flow trendline
// Cumulative flow colours
const COLOR_CUM_INFLOW = "#00acdc"; // blue
const COLOR_CUM_THROUGHPUT = "#94ba33"; // green
const COLOR_CUM_SCHEDULED = "#fb923c"; // orange — scheduled-per-day series
// Scheduling state colours
const COLOR_SCHEDULED = "#94ba33"; // green
const COLOR_UNSCHEDULED = "#F8ED00"; // yellow
const COLOR_AFTER_DEADLINE = "#d71433"; // red
const COLOR_AWAITING_QA = "#00acdc"; // blue

// penaltyExposure comes from the API in cents (inspection-type prices are stored in cents).
const fmtMoney = (cents) =>
  "$" + Number(Math.round((cents || 0) / 100)).toLocaleString(undefined, { maximumFractionDigits: 0 });

const fmt1 = (v) => typeof v === "number" ? v.toFixed(1) : "0";

// Least-squares trendline over a series — shows whether net flow is trending up (backlog
// growing) or down (catching up) across the 30-day window.
const linearTrend = (vals) => {
  const n = vals.length;
  if (n < 2) {
    return vals.map(() => vals[0] || 0);
  }
  let sx = 0, sy = 0, sxx = 0, sxy = 0;
  for (let i = 0; i < n; i++) {
    sx += i;
    sy += vals[i];
    sxx += i * i;
    sxy += i * vals[i];
  }
  const denom = n * sxx - sx * sx;
  if (denom === 0) {
    return vals.map(() => sy / n);
  }
  const slope = (n * sxy - sx * sy) / denom;
  const intercept = (sy - slope * sx) / n;
  return vals.map((_, i) => slope * i + intercept);
};

const PipelineTab = () => {
  const { data: pipelineResp, error: pipelineErr } = useSWR("pipeline", () => apiReports.getPipeline(), { refreshInterval: 30000 });

  const loading = !pipelineResp && !pipelineErr;
  const r = pipelineResp?.data?.result;
  const [drilldown, setDrilldown] = useState(null); // null | "outside" | "exposure" | "scheduled" | "submitted"

  // --- Inflow vs throughput (mixed bar + line) ---
  const flowChart = useMemo(() => {
    if (!r) {
      return null;
    }
    const flow = r.flow || [];
    const labels = flow.map(d => `Day ${d.day}`);
    const netFlow = flow.map(d => d.netFlow);
    return {
      labels,
      datasets: [
        {
          type: "bar", label: "New (incoming)", data: flow.map(d => d.newCount),
          backgroundColor: COLOR_NEW, yAxisID: "y-inflow", stack: "inflow", order: 4,
        },
        {
          type: "bar", label: "Renewal (incoming)", data: flow.map(d => d.renewalCount),
          backgroundColor: COLOR_RENEWAL, yAxisID: "y-inflow", stack: "inflow", order: 4,
        },
        {
          type: "line", label: "Throughput (submitted)", data: flow.map(d => d.throughput),
          borderColor: COLOR_THROUGHPUT, backgroundColor: COLOR_THROUGHPUT, yAxisID: "y-flow",
          borderWidth: 2, pointRadius: 0, tension: 0.2, fill: false, order: 3,
        },
        {
          type: "line", label: "Scheduled (first appt set)", data: flow.map(d => d.scheduled || 0),
          borderColor: COLOR_CUM_SCHEDULED, backgroundColor: COLOR_CUM_SCHEDULED, yAxisID: "y-flow",
          borderWidth: 2, pointRadius: 0, tension: 0.2, fill: false, order: 3,
        },
        {
          type: "line", label: "Net flow (incoming − throughput)", data: netFlow,
          borderColor: COLOR_NETFLOW, backgroundColor: COLOR_NETFLOW, yAxisID: "y-flow",
          borderWidth: 2, pointRadius: 0, tension: 0.2, fill: false, order: 2,
        },
        {
          type: "line", label: "Net flow trend", data: linearTrend(netFlow),
          borderColor: COLOR_TREND, backgroundColor: COLOR_TREND, borderDash: [6, 4],
          yAxisID: "y-flow", borderWidth: 2, pointRadius: 0, fill: false, order: 1,
        },
      ],
    };
  }, [r]);

  const flowOptions = useMemo(() => ({
    maintainAspectRatio: false,
    legend: { position: "top" },
    tooltips: { mode: "index", intersect: false },
    scales: {
      xAxes: [{ stacked: true, ticks: { autoSkip: true, maxTicksLimit: 12 } }],
      yAxes: [
        {
          id: "y-inflow", stacked: true, position: "left",
          ticks: { beginAtZero: true, precision: 0 },
          scaleLabel: { display: true, labelString: "Incoming" },
        },
        {
          id: "y-flow", stacked: false, position: "right",
          ticks: { beginAtZero: true, precision: 0 },
          gridLines: { drawOnChartArea: false },
          scaleLabel: { display: true, labelString: "Throughput / Scheduled / Net flow" },
        },
      ],
    },
  }), []);

  // --- Cumulative inflow vs throughput (increasing lines, like the Scheduling tab trend) ---
  // Hover shows the running total plus that day's figure.
  const cumChart = useMemo(() => {
    if (!r) {
      return null;
    }
    const flow = r.flow || [];
    let cumInflow = 0, cumThroughput = 0, cumScheduled = 0;
    const inflow = [], throughput = [], scheduled = [];
    flow.forEach(d => {
      cumInflow += d.inflow || 0;
      cumThroughput += d.throughput || 0;
      cumScheduled += d.scheduled || 0;
      inflow.push(cumInflow);
      throughput.push(cumThroughput);
      scheduled.push(cumScheduled);
    });
    const labels = flow.map(d => String(d.day));
    const names = ["Incoming", "Throughput", "Scheduled"];
    const dailyKeys = ["inflow", "throughput", "scheduled"];
    return {
      data: {
        labels,
        datasets: [
          {
            label: "Cumulative Incoming (New + Renewal)", data: inflow,
            borderColor: COLOR_CUM_INFLOW, backgroundColor: COLOR_CUM_INFLOW,
            borderWidth: 2.5, pointRadius: 0, tension: 0.2, fill: false,
          },
          {
            label: "Cumulative Throughput (Submitted)", data: throughput,
            borderColor: COLOR_CUM_THROUGHPUT, backgroundColor: COLOR_CUM_THROUGHPUT,
            borderWidth: 2.5, pointRadius: 0, tension: 0.2, fill: false,
          },
          {
            label: "Cumulative Scheduled (first appt set)", data: scheduled,
            borderColor: COLOR_CUM_SCHEDULED, backgroundColor: COLOR_CUM_SCHEDULED,
            borderWidth: 2.5, pointRadius: 0, tension: 0.2, fill: false,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        legend: { position: "top" },
        tooltips: {
          mode: "index", intersect: false,
          callbacks: {
            title: (items) => `Day ${items[0].label}`,
            label: (item) => {
              const idx = item.index;
              const cum = Math.round(item.yLabel);
              const di = item.datasetIndex;
              const daily = flow[idx]?.[dailyKeys[di]] ?? 0;
              const name = names[di] || `Series ${di}`;
              return `${name}: ${cum.toLocaleString()} cumulative (+${daily} that day)`;
            },
          },
        },
        scales: {
          xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 12 }, scaleLabel: { display: true, labelString: "Day (last 30 days)" } }],
          yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: "Cumulative inspections" } }],
        },
      },
    };
  }, [r]);

  // --- Scheduling state breakdown (doughnut) ---
  const stageChart = useMemo(() => {
    if (!r) {
      return null;
    }
    const s = r.stageCounts || {};
    return {
      labels: ["Scheduled (within deadline)", "Unscheduled", "Scheduled after deadline", "Awaiting QA"],
      datasets: [{
        data: [
          s.scheduledWithinDeadline,
          (s.receivedNotContacted || 0) + (s.contactInProgress || 0),
          s.scheduledAfterDeadline,
          s.awaitingQA,
        ],
        backgroundColor: [COLOR_SCHEDULED, COLOR_UNSCHEDULED, COLOR_AFTER_DEADLINE, COLOR_AWAITING_QA],
      }],
    };
  }, [r]);

  // --- Forward deadline wave (stacked bar by bucket) ---
  const waveChart = useMemo(() => {
    if (!r) {
      return null;
    }
    const buckets = r.deadlineBuckets || [];
    return {
      labels: buckets.map(b => b.bucket === "pastDue" ? "Past due" : b.bucket),
      datasets: [
        { label: "Scheduled", data: buckets.map(b => b.scheduled), backgroundColor: COLOR_SCHEDULED, stack: "wave" },
        { label: "Unscheduled", data: buckets.map(b => b.unscheduled), backgroundColor: COLOR_UNSCHEDULED, stack: "wave" },
        { label: "Scheduled after deadline", data: buckets.map(b => b.scheduledAfterDeadline), backgroundColor: COLOR_AFTER_DEADLINE, stack: "wave" },
      ],
    };
  }, [r]);

  const waveOptions = useMemo(() => ({
    maintainAspectRatio: false,
    legend: { position: "top" },
    tooltips: { mode: "index", intersect: false },
    scales: {
      xAxes: [{ stacked: true }],
      yAxes: [{ stacked: true, ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: "Inspections" } }],
    },
  }), []);

  if (loading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-py-24">
        <CircularProgress />
      </div>
    );
  }

  if (!r) {
    return (
      <p className="tw-text-gray-500 tw-text-sm">
        {pipelineErr ? `Unable to load pipeline data: ${pipelineErr.message}` : "No pipeline data available."}
      </p>
    );
  }

  const k = r.kpis || {};

  // % Outside of Deadline (flipped from % within): outside = active − within.
  const activeTotal = k.activeInPipeline ?? 0;
  const withinCount = k.withinDeadlineCount ?? 0;
  const outsideCount = Math.max(activeTotal - withinCount, 0);
  const pctOutside = activeTotal > 0 ? outsideCount / activeTotal * 100 : 0;

  // Month names for the carried-into-this-month card.
  const _now = new Date();
  const thisMonthName = _now.toLocaleString(undefined, { month: "long" });
  const prevMonthName = new Date(_now.getFullYear(), _now.getMonth() - 1, 1).toLocaleString(undefined, { month: "long" });

  // Past-7-day figures (last 7 entries of the 30-day flow series; today is partial/live).
  const last7 = (r.flow || []).slice(-7);
  const incoming7d = last7.reduce((s, d) => s + (d.inflow || 0), 0);
  const scheduled7d = last7.reduce((s, d) => s + (d.scheduled || 0), 0);
  const submitted7d = last7.reduce((s, d) => s + (d.throughput || 0), 0);
  const pctOfIncoming = (n) => incoming7d > 0 ? `${Math.round(n / incoming7d * 100)}% of incoming` : "—";

  const isTableDrilldown = drilldown === "outside" || drilldown === "exposure";
  const drillItems = drilldown === "outside" ? r.outsideDeadlineItems || [] : r.projectedLateItems || [];
  const drillTitle = {
    outside: "Outside of Deadline",
    exposure: "Projected Late (Penalty Exposure)",
    scheduled: "Scheduled — who set the appointment (last 30 days)",
    submitted: "Submitted (7d) — by person",
  }[drilldown] || "";

  const breakdownChart = (items) => ({
    labels: (items || []).map(b => b.name),
    datasets: [{ label: "Inspections", data: (items || []).map(b => b.count), backgroundColor: "#00acdc" }],
  });
  const breakdownOptions = {
    maintainAspectRatio: false,
    legend: { display: false },
    scales: { xAxes: [{ ticks: { beginAtZero: true, precision: 0 } }] },
  };

  // Stacked scheduled-by-user chart: one dataset per week bucket, legend toggles each; all on = 30-day total.
  const SCHED_BUCKET_COLORS = ["#d71433", "#00acdc", "#94ba33", "#fbbf24"]; // red / blue / green / yellow — distinct per week bucket
  const scheduledBucketChart = (rows) => ({
    labels: (rows || []).map(rw => rw.name),
    datasets: [
      { label: "0–7 days", data: (rows || []).map(rw => rw.week07), backgroundColor: SCHED_BUCKET_COLORS[0], stack: "sched" },
      { label: "7–14 days", data: (rows || []).map(rw => rw.week714), backgroundColor: SCHED_BUCKET_COLORS[1], stack: "sched" },
      { label: "14–21 days", data: (rows || []).map(rw => rw.week1421), backgroundColor: SCHED_BUCKET_COLORS[2], stack: "sched" },
      { label: "21–30 days", data: (rows || []).map(rw => rw.week2130), backgroundColor: SCHED_BUCKET_COLORS[3], stack: "sched" },
    ],
  });
  const scheduledBucketOptions = {
    maintainAspectRatio: false,
    legend: { position: "top" },
    scales: { xAxes: [{ stacked: true, ticks: { beginAtZero: true, precision: 0 } }], yAxes: [{ stacked: true }] },
    tooltips: { mode: "index", intersect: false },
  };

  const toggle = (which) => () => setDrilldown(drilldown === which ? null : which);

  return (
    <>
      <p className="tw-text-xs tw-text-gray-500 tw--mt-2 tw-mb-2">
        New Contract only — inspections received on or after 1 Jul 2026. Old-contract inspections are tracked on the Penalty Risk tab.
      </p>

      {/* KPI cards */}
      <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-x-4 md:tw-gap-x-8 xl:tw-gap-x-16 tw-gap-y-2 tw-my-4">
        <DashboardCard title="Active in Pipeline" value={String(k.activeInPipeline ?? 0)} color="tw-bg-primary" secondValue={`New: ${k.activeNew ?? 0} · Renewal: ${k.activeRenewal ?? 0}`} />
        <DashboardCard
          title="Avg Days Remaining" value={fmt1(k.avgDaysRemaining)} color="tw-bg-fs-green"
          secondValue={`New: ${fmt1(k.avgDaysRemainingNew)} · Renewal: ${fmt1(k.avgDaysRemainingRenewal)}`}
        />
        <DashboardCard
          title="Avg Days Overdue" value={fmt1(k.avgDaysOverdue)} color="tw-bg-fs-red"
          secondValue={`New: ${fmt1(k.avgDaysOverdueNew)} · Renewal: ${fmt1(k.avgDaysOverdueRenewal)}`}
        />
        <DashboardCard
          title={`% Outside of Deadline ${drilldown === "outside" ? "▴" : "▾"}`} value={`${pctOutside.toFixed(0)}%`} color="tw-bg-fs-dark-blue-light"
          secondValue={`${outsideCount} of ${activeTotal} inspections`}
          onClick={toggle("outside")}
        />
        <DashboardCard
          title={`Penalty Exposure ${drilldown === "exposure" ? "▴" : "▾"}`} value={fmtMoney(k.penaltyExposure)} color="tw-bg-fs-red-dark"
          secondValue={`${k.projectedLateCount ?? 0} inspections at risk`}
          onClick={toggle("exposure")}
        />
        <DashboardCard
          title={`Carried into ${thisMonthName}`} value={String(k.carriedFromLastMonth ?? 0)} color="tw-bg-gray-400"
          secondValue={`open from ${prevMonthName} at month start`}
        />
        <DashboardCard title="Incoming (7d)" value={String(incoming7d)} color="tw-bg-primary" secondValue={`avg ${Math.round(incoming7d / 7)}/day`} />
        <DashboardCard
          title={`Scheduled (7d) ${drilldown === "scheduled" ? "▴" : "▾"}`} value={String(scheduled7d)} color="tw-bg-yellow-400"
          secondValue={`${pctOfIncoming(scheduled7d)} · avg ${Math.round(scheduled7d / 7)}/day`} onClick={toggle("scheduled")}
        />
        <DashboardCard
          title={`Submitted (7d) ${drilldown === "submitted" ? "▴" : "▾"}`} value={String(submitted7d)} color="tw-bg-fs-green"
          secondValue={`${pctOfIncoming(submitted7d)} · avg ${Math.round(submitted7d / 7)}/day`} onClick={toggle("submitted")}
        />
      </div>

      {/* Expand-down drilldown from a clicked KPI card */}
      {drilldown && 
        <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4 tw-mb-6">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
            <h4 className="tw-text-lg tw-font-bold">
              {drillTitle}
              {isTableDrilldown && <span className="tw-text-sm tw-font-normal tw-text-gray-500"> ({drillItems.length} inspections)</span>}
            </h4>
            <button className="tw-text-sm tw-text-primary hover:tw-underline" onClick={() => setDrilldown(null)}>Close</button>
          </div>
          {isTableDrilldown && (drillItems.length === 0 ? 
            <p className="tw-text-gray-500 tw-text-sm">No inspections in this list.</p>
            : 
            <div className="tw-overflow-x-auto">
              <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
                <thead>
                  <tr className="tw-bg-fs-dark-blue tw-bg-opacity-25">
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Policy #</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Type</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Status</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Policy</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Location</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Age</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Days Left</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Appt</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Contacts</th>
                  </tr>
                </thead>
                <tbody>
                  {drillItems.map((item, i) => 
                    <tr key={`${item.policyNo}-${i}`} className={i % 2 ? "tw-bg-gray-200" : ""}>
                      <td className="tw-px-4 tw-py-2">
                        <a href={`/inspections/${item.policyNo}/process/pre_r`} target="_blank" rel="noopener noreferrer" className="tw-text-primary hover:tw-underline">
                          {item.policyNo}
                        </a>
                      </td>
                      <td className="tw-px-4 tw-py-2">{item.inspectionType || "—"}</td>
                      <td className="tw-px-4 tw-py-2">{item.prettyStatus || item.status}</td>
                      <td className="tw-px-4 tw-py-2">{item.policyStatus}</td>
                      <td className="tw-px-4 tw-py-2">{item.city ? `${item.city}, ${item.state}` : item.state || "—"}</td>
                      <td className="tw-px-4 tw-py-2">{item.ageDays}d</td>
                      <td className="tw-px-4 tw-py-2">
                        <span className={item.daysRemaining <= 3 ? "tw-text-fs-red tw-font-bold" : item.daysRemaining <= 8 ? "tw-text-fs-red-light tw-font-semibold" : ""}>
                          {item.daysRemaining}
                        </span>
                      </td>
                      <td className="tw-px-4 tw-py-2">
                        {item.appointmentDate && new Date(item.appointmentDate).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0)
                          ? new Date(item.appointmentDate).toLocaleDateString() : "—"}
                      </td>
                      <td className="tw-px-4 tw-py-2">{item.contactAttempts || 0}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {drilldown === "scheduled" && 
            <div style={{ height: `${Math.max((r.scheduledByUser || []).length * 32, 120)}px` }}>
              <HorizontalBar data={scheduledBucketChart(r.scheduledByUser)} options={scheduledBucketOptions} />
            </div>
          }
          {drilldown === "scheduled" && 
            <p className="tw-text-xs tw-text-gray-500 tw-mt-2">Stacked by week bucket (0–7, 7–14, 14–21, 21–30 days ago). Toggle a bucket in the legend to isolate it; all on = 30-day total.</p>
          }
          {drilldown === "submitted" && 
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6">
              <div>
                <h5 className="tw-text-sm tw-font-semibold tw-mb-1">By who submitted</h5>
                <div style={{ height: `${Math.max((r.submittedByUser || []).length * 32, 120)}px` }}>
                  <HorizontalBar data={breakdownChart(r.submittedByUser)} options={breakdownOptions} />
                </div>
              </div>
              <div>
                <h5 className="tw-text-sm tw-font-semibold tw-mb-1">By assigned inspector</h5>
                <div style={{ height: `${Math.max((r.submittedByAssignee || []).length * 32, 120)}px` }}>
                  <HorizontalBar data={breakdownChart(r.submittedByAssignee)} options={breakdownOptions} />
                </div>
              </div>
            </div>
          }
        </div>
      }

      {/* Inflow vs throughput + scheduling state */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6 tw-mb-6">
        <div className="lg:tw-col-span-2 tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4">
          <h4 className="tw-text-lg tw-font-bold tw-mb-2">Incoming vs Throughput (last 30 days)</h4>
          <div style={{ height: "320px" }}>
            {flowChart ? <Bar data={flowChart} options={flowOptions} /> : <p className="tw-text-gray-500 tw-text-sm">No flow data.</p>}
          </div>
        </div>
        <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4">
          <h4 className="tw-text-lg tw-font-bold tw-mb-2">Scheduling State</h4>
          <div style={{ height: "320px" }}>
            {stageChart ? <Doughnut data={stageChart} options={{ legend: { position: "right" }, maintainAspectRatio: false }} /> : <p className="tw-text-gray-500 tw-text-sm">No data.</p>}
          </div>
        </div>
      </div>

      {/* Cumulative inflow vs throughput (increasing lines) */}
      <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4 tw-mb-6">
        <h4 className="tw-text-lg tw-font-bold tw-mb-2">Cumulative Incoming vs Throughput (last 30 days)</h4>
        <p className="tw-text-sm tw-text-gray-600 tw-mb-2">
          Running totals — are we keeping up with incoming? Incoming above Throughput means the backlog is growing.
        </p>
        <div style={{ height: "300px" }}>
          {cumChart ? <Line data={cumChart.data} options={cumChart.options} /> : <p className="tw-text-gray-500 tw-text-sm">No flow data.</p>}
        </div>
      </div>

      {/* Forward deadline wave */}
      <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4 tw-mb-6">
        <h4 className="tw-text-lg tw-font-bold tw-mb-2">Forward Deadline Wave</h4>
        <div style={{ height: "280px" }}>
          {waveChart ? <Bar data={waveChart} options={waveOptions} /> : <p className="tw-text-gray-500 tw-text-sm">No data.</p>}
        </div>
      </div>
    </>
  );
};

export default PipelineTab;